import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, ArrowLeft, Loader2, CheckCircle, Mail, 
  BookOpen, TrendingUp, Users, Shield, Lightbulb, Target,
  BarChart3, Calendar
} from 'lucide-react';
import { requestReportAccess } from '../lib/api';
import { trackPageView } from '../lib/ga4';

const RapportPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    trackPageView('/rapport', 'Rapport IA PME');
  }, []);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Veuillez entrer votre email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Veuillez entrer un email valide');
      return;
    }

    setIsLoading(true);

    try {
      await requestReportAccess(email);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Report access error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Stat card component
  const StatCard = ({ value, label }) => (
    <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
      <p className="text-2xl font-bold" style={{ color: '#1C2E4A' }}>{value}</p>
      <p className="text-sm text-gray-600 mt-1">{label}</p>
    </div>
  );

  // Report content preview items
  const reportContents = [
    { icon: BarChart3, text: "État des lieux complet de l'adoption IA en France (données 2024-2025)" },
    { icon: Target, text: "Analyse des freins structurels, psychologiques et réglementaires" },
    { icon: TrendingUp, text: "Les leviers et financements disponibles (Bpifrance, OPCO, France 2030)" },
    { icon: Users, text: "Impact sur l'emploi : 11M de postes créés vs 9M obsolètes d'ici 2030" },
    { icon: Lightbulb, text: "Les 5 technologies IA à surveiller (2025-2030)" },
    { icon: Shield, text: "Recommandations stratégiques pour dirigeants et salariés" },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }} data-testid="rapport-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="rapport-back-link"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      {/* Report Header */}
      <div className="text-white py-16 px-4" style={{ backgroundColor: '#1C2E4A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            L'Adoption de l'Intelligence Artificielle par les Entreprises Françaises en 2025
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Rapport stratégique et prospectif | 25 pages | Février 2026
          </p>
          <p className="text-blue-200 text-sm mt-2">
            Sources : INSEE, Bpifrance, France Num, OCDE, PwC, McKinsey, World Economic Forum, Deloitte, Adecco
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {!isSubmitted ? (
          /* Request form */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-8">
              {/* Report highlights */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Ce rapport de 25 pages couvre :
                </h2>
                <div className="space-y-4">
                  {reportContents.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1C2E4A' }}>
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-700 pt-2">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key stats preview */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <StatCard value="31 %" label="TPE-PME utilisent l'IA" />
                <StatCard value="+56 %" label="Écart salarial pro-IA" />
                <StatCard value="11M" label="Postes IA créés d'ici 2030" />
              </div>

              {/* Email form */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1C2E4A' }}>
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Recevez le rapport complet par email
                    </h3>
                    <p className="text-sm text-gray-600">
                      Gratuit et sans engagement
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="sr-only">
                      Email professionnel
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.fr"
                      className={`bg-white text-base py-3 ${error ? 'border-red-500' : ''}`}
                      data-testid="rapport-email-input"
                      aria-invalid={!!error}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-red-600" role="alert">
                        {error}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-white rounded-full py-3 text-base font-semibold"
                    data-testid="rapport-submit-btn"
                    style={{ backgroundColor: '#1C2E4A' }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Recevoir le rapport par email
                      </>
                    )}
                  </Button>
                </form>

                <p className="mt-4 text-xs text-gray-500 text-center">
                  Nous respectons votre vie privée. Votre email ne sera jamais partagé ni revendu.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Confirmation message */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="rapport-confirmation">
            <div className="p-8 md:p-12 text-center">
              {/* Success icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Votre rapport est en route !
              </h2>
              
              <p className="text-lg text-gray-600 mb-2">
                Nous venons d'envoyer le rapport complet à :
              </p>
              <p className="text-xl font-semibold mb-8" style={{ color: '#1C2E4A' }}>
                {email}
              </p>

              {/* Email tips */}
              <div className="bg-blue-50 rounded-xl p-6 text-left mb-8 max-w-lg mx-auto">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Vérifiez votre boîte de réception
                </h3>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-0.5">•</span>
                    Le rapport arrive généralement sous 2-3 minutes
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-0.5">•</span>
                    Pensez à vérifier vos spams ou courriers indésirables
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-0.5">•</span>
                    L'expéditeur sera <strong>ACDC-Formation</strong>
                  </li>
                </ul>
              </div>

              {/* What's next */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  En attendant votre rapport...
                </h3>
                <p className="text-gray-600 mb-6">
                  Découvrez comment nous pouvons vous aider à structurer l'IA dans votre entreprise
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/#diagnostic">
                    <Button
                      className="text-white rounded-full py-3 px-6"
                      style={{ backgroundColor: '#1C2E4A' }}
                    >
                      Faire le diagnostic gratuit
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="rounded-full py-3 px-6"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trust elements */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Rapport téléchargé par plus de <strong>500 dirigeants</strong> depuis janvier 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default RapportPage;
